/**
 * Smart Sync Options Modal
 * Intelligent, multi-tenant safe syncing with preview and history
 * Supports multiple email platforms (GetResponse, EmailOctopus, etc.)
 */

import * as React from 'react';
import { useState, useEffect } from 'react';
import { useAuthStore } from '@store/authStore';
import { api } from '../../services/api';
import {
  X,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Send,
  Users,
  ShoppingCart,
  Package,
  MessageSquare,
  Clock,
  TrendingUp,
  Database,
  Mail,
  Zap,
  Eye,
  History,
  Shield,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

interface SmartSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  eshopId: string;
  eshopName: string;
  companyId: string;
  onSyncComplete?: () => void; // Optional callback
}

interface EmailPlatform {
  id: string;
  name: string;
  type: string;
  icon: string;
}

interface SyncPreviewData {
  customers: { total: number; new: number; updated: number };
  carts: { total: number; abandoned: number };
  consultations: { total: number; abandoned: number };
  estimatedDuration: string;
}

const SmartSyncModal: React.FC<SmartSyncModalProps> = ({
  isOpen,
  onClose,
  eshopId,
  eshopName,
  companyId,
  onSyncComplete,
}) => {
  const { accessToken } = useAuthStore();

  // State
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStatus, setSyncStatus] = useState('');
  const [syncResult, setSyncResult] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Available platforms
  const [availablePlatforms, setAvailablePlatforms] = useState<EmailPlatform[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  
  // Sync options
  const [syncCustomers, setSyncCustomers] = useState(true);
  const [syncCarts, setSyncCarts] = useState(true);
  const [syncAbandonedCarts, setSyncAbandonedCarts] = useState(true);
  const [syncConsultations, setSyncConsultations] = useState(true);
  const [forceFullSync, setForceFullSync] = useState(false);
  
  // Preview data
  const [previewData, setPreviewData] = useState<SyncPreviewData | null>(null);
  const [syncHistory, setSyncHistory] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchAvailablePlatforms();
      fetchSyncPreview();
      fetchSyncHistory();
    }
  }, [isOpen, eshopId, companyId]);

  const fetchAvailablePlatforms = async () => {
    try {
      const response = await fetch(
        `/api/v1/eshops/${eshopId}/integration-settings?companyId=${companyId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.ok) {
        const data = await response.json();
        const platforms: EmailPlatform[] = [];

        if (data.data.getresponseDataSourceId) {
          platforms.push({
            id: data.data.getresponseDataSourceId,
            name: 'GetResponse',
            type: 'getresponse',
            icon: '📧',
          });
        }

        if (data.data.emailoctopusDataSourceId) {
          platforms.push({
            id: data.data.emailoctopusDataSourceId,
            name: 'EmailOctopus',
            type: 'emailoctopus',
            icon: '🐙',
          });
        }

        setAvailablePlatforms(platforms);
        // Auto-select all platforms
        setSelectedPlatforms(platforms.map(p => p.id));
      }
    } catch (error) {
      console.error('Error fetching platforms:', error);
    }
  };

  const fetchSyncPreview = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/v1/eshops/${eshopId}/sync-preview?companyId=${companyId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data.data);
      }
    } catch (error) {
      console.error('Error fetching preview:', error);
      // Set default preview if API fails
      setPreviewData({
        customers: { total: 0, new: 0, updated: 0 },
        carts: { total: 0, abandoned: 0 },
        consultations: { total: 0, abandoned: 0 },
        estimatedDuration: '~2 minutes',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncHistory = async () => {
    try {
      const response = await fetch(
        `/api/v1/eshops/${eshopId}/sync-history?companyId=${companyId}&limit=10`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setSyncHistory(data.data?.history || []);
      }
    } catch (error) {
      console.error('Error fetching sync history:', error);
    }
  };

  const handleSmartSync = async () => {
    const dataTypes = [];
    if (syncCustomers) dataTypes.push('Customers');
    if (syncCarts) dataTypes.push('Shopping Carts');
    if (syncAbandonedCarts) dataTypes.push('Abandoned Carts');
    if (syncConsultations) dataTypes.push('Consultations');

    if (dataTypes.length === 0) {
      alert('⚠️ Please select at least one data type to sync');
      return;
    }

    const platformNames = availablePlatforms
      .filter(p => selectedPlatforms.includes(p.id))
      .map(p => p.name)
      .join(', ');

    const confirmed = confirm(
      `🚀 SMART SYNC CONFIRMATION\n\n` +
        `📦 E-shop: ${eshopName}\n` +
        `🏢 Company: Isolated & secure\n\n` +
        `📊 Data to sync:\n${dataTypes.map(dt => `  ✓ ${dt}`).join('\n')}\n\n` +
        `📧 Target platform(s):\n${selectedPlatforms.map((id, i) => `  ${availablePlatforms.find(p => p.id === id)?.icon} ${availablePlatforms.find(p => p.id === id)?.name}`).join('\n')}\n\n` +
        (forceFullSync ? `🔄 FORCE FULL SYNC: ALL customers will be re-processed\n\n` : '') +
        `⏱️ Estimated time: ${forceFullSync ? '~30-60 seconds' : previewData?.estimatedDuration || '~2 min'}\n\n` +
        `🔒 Multi-tenant safe: Only ${eshopName} data will be synced\n\n` +
        `Continue?`
    );

    if (!confirmed) return;

    try {
      setSyncing(true);
      setSyncProgress(0);
      setSyncStatus('Initializing sync...');
      setSyncResult(null);

      // Build sync parameters
      const syncParams = new URLSearchParams({
        companyId,
        syncCustomers: syncCustomers.toString(),
        syncCarts: syncCarts.toString(),
        syncAbandonedCarts: syncAbandonedCarts.toString(),
        syncConsultations: syncConsultations.toString(),
        targetPlatforms: selectedPlatforms.join(','),
        forceFullSync: forceFullSync.toString(),
      });

      // Smooth progress simulation (doesn't stop at 90%)
      let currentProgress = 0;
      const progressInterval = setInterval(() => {
        currentProgress += Math.random() * 15; // Random progress 0-15%
        if (currentProgress > 95) currentProgress = 95; // Cap at 95% until complete
        setSyncProgress(Math.floor(currentProgress));
      }, 400);

      const steps = [];
      if (syncCustomers) steps.push('Syncing customers');
      if (syncCarts) steps.push('Syncing carts');
      if (syncAbandonedCarts) steps.push('Processing abandoned carts');
      if (syncConsultations) steps.push('Syncing consultations');

      let stepIndex = 0;
      const statusInterval = setInterval(() => {
        if (stepIndex < steps.length) {
          setSyncStatus(steps[stepIndex]);
          stepIndex++;
        }
      }, 500);

      const response = await fetch(
        `/api/v1/eshops/${eshopId}/smart-sync?${syncParams}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      clearInterval(progressInterval);
      clearInterval(statusInterval);

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.status}`);
      }

      const result = await response.json();
      setSyncProgress(100);
      setSyncStatus('✅ Sync completed!');
      
      // Extract results properly
      const syncedData = result.data?.synced || result.data || {};
      const syncResult = {
        customers: syncedData.customers || 0,
        carts: syncedData.carts || 0,
        abandonedCarts: syncedData.abandonedCarts || 0,
        consultations: syncedData.consultations || 0,
      };
      
      setSyncResult(syncResult);

      // Auto-close after 2 seconds
      setTimeout(async () => {
        await fetchSyncHistory();
        if (onSyncComplete) {
          onSyncComplete();
        }
        onClose();
      }, 2000);
    } catch (error: any) {
      setSyncStatus(`❌ Error: ${error.message}`);
      setSyncProgress(0);
      alert(`❌ Sync failed: ${error.message}`);
    } finally {
      setTimeout(() => setSyncing(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-indigo-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Smart Sync Options
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Configure intelligent multi-platform sync for {eshopName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Multi-Tenant Security Badge */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-semibold text-green-900 dark:text-green-100">
                  🔒 Multi-Tenant Isolation Active
                </p>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Company ID: {companyId} • E-shop: {eshopName} • Data is isolated and secure
                </p>
              </div>
            </div>
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Database className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900 dark:text-blue-100">
                  PostgreSQL → MongoDB Sync
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Syncs e-shop data from PostgreSQL to Marketing Analytics database. Email platform sync (GetResponse, etc.) is handled separately in the <strong>Email Marketing</strong> module.
                </p>
              </div>
            </div>
          </div>

          {/* Data Selection */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Database className="w-5 h-5 text-indigo-600" />
              Select Data to Sync
            </h3>
            
            <div className="space-y-3">
              {/* Customers */}
              <label className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncCustomers}
                  onChange={e => setSyncCustomers(e.target.checked)}
                  className="w-5 h-5 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 mt-0.5"
                />
                <Users className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">Customers</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    All customer profiles with emails, names, phone, DOB, gender, and order history
                  </p>
                  {previewData && (
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                      📊 {previewData.customers.total} total ({previewData.customers.new} new, {previewData.customers.updated} updates)
                    </p>
                  )}
                </div>
              </label>

              {/* Shopping Carts */}
              <label className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncCarts}
                  onChange={e => setSyncCarts(e.target.checked)}
                  className="w-5 h-5 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 mt-0.5"
                />
                <ShoppingCart className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">Shopping Carts (All)</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    All shopping carts including completed orders
                  </p>
                  {previewData && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      📊 {previewData.carts.total} total carts
                    </p>
                  )}
                </div>
              </label>

              {/* Abandoned Carts */}
              <label className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncAbandonedCarts}
                  onChange={e => setSyncAbandonedCarts(e.target.checked)}
                  className="w-5 h-5 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 mt-0.5"
                />
                <Package className="w-5 h-5 text-orange-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">Abandoned Carts</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Incomplete purchases for cart recovery campaigns
                  </p>
                  {previewData && previewData.carts.abandoned > 0 && (
                    <div className="mt-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded px-2 py-1">
                      <p className="text-xs text-orange-800 dark:text-orange-200 font-semibold">
                        🎯 RECOMMENDED: {previewData.carts.abandoned} abandoned carts ready for recovery!
                      </p>
                    </div>
                  )}
                </div>
              </label>

              {/* Consultations */}
              <label className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncConsultations}
                  onChange={e => setSyncConsultations(e.target.checked)}
                  className="w-5 h-5 text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 mt-0.5"
                />
                <MessageSquare className="w-5 h-5 text-purple-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">Abandoned Consultations</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Incomplete medical consultations for follow-up
                  </p>
                  {previewData && (
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                      📊 {previewData.consultations.abandoned} abandoned consultations
                    </p>
                  )}
                </div>
              </label>

              {/* Force Full Sync - ADVANCED OPTION */}
              <div className="border-2 border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={forceFullSync}
                    onChange={e => setForceFullSync(e.target.checked)}
                    className="w-5 h-5 text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border-red-400 dark:border-red-500 rounded focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 mt-0.5"
                  />
                  <RefreshCw className="w-5 h-5 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-bold text-red-900 dark:text-red-100 text-lg">⚠️ Force Full Sync (DROPS ALL DATA)</p>
                    <p className="text-sm text-red-700 dark:text-red-300 font-semibold mt-1">
                      ❌ This will DELETE all {previewData?.currentCustomers.toLocaleString() || '27,488'} customers and re-sync from scratch!
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                      <strong>⏱️ Takes 30-60 seconds</strong> • Only use when database schema changes or data is corrupted
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300 mt-2 bg-green-100 dark:bg-green-900/40 px-2 py-1 rounded">
                      ✅ <strong>Default mode (unchecked):</strong> Only syncs NEW/CHANGED customers (~5-10 seconds)
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Data Preview Toggle */}
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-blue-900 dark:text-blue-100">
                Sync Preview
              </span>
            </div>
            {showPreview ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {/* Preview Details */}
          {showPreview && previewData && (
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {previewData.customers.total}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Customers</p>
                </div>
                <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <ShoppingCart className="w-5 h-5 text-green-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {previewData.carts.total}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Carts</p>
                </div>
                <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <Package className="w-5 h-5 text-orange-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {previewData.carts.abandoned}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Abandoned</p>
                </div>
                <div className="text-center p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {previewData.consultations.abandoned}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Consults</p>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                <span className="text-indigo-800 dark:text-indigo-200">⏱️ Estimated Duration:</span>
                <span className="font-semibold text-indigo-900 dark:text-indigo-100">{previewData.estimatedDuration}</span>
              </div>
            </div>
          )}

          {/* Sync History Toggle */}
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
          >
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <span className="font-medium text-gray-900 dark:text-white">
                Sync History ({syncHistory.length})
              </span>
            </div>
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {/* History List */}
          {showHistory && (
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 max-h-64 overflow-y-auto">
              {syncHistory.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No sync history yet</p>
              ) : (
                <div className="space-y-2">
                  {syncHistory.map((record, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {record.platform || 'All Platforms'}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {new Date(record.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-green-600">
                          {record.status === 'success' ? '✅' : '❌'} {record.recordsSynced || 0} records
                        </p>
                        <p className="text-xs text-gray-500">{record.duration}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Progress Bar (shown during sync) */}
          {syncing && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-6 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-indigo-600 dark:text-indigo-400 animate-spin" />
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {syncStatus}
                  </span>
                </div>
                <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {syncProgress}%
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden shadow-inner">
                <div
                  className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-300 ease-out flex items-center justify-end pr-2"
                  style={{ width: `${syncProgress}%` }}
                >
                  {syncProgress > 10 && (
                    <span className="text-xs font-bold text-white drop-shadow">
                      {syncProgress}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Sync Results (shown after completion) */}
          {syncResult && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                <h3 className="text-lg font-bold text-green-900 dark:text-green-100">
                  ✅ Sync Completed Successfully!
                </h3>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-green-200 dark:border-green-700">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {syncResult.customers}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Customers</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-green-200 dark:border-green-700">
                  <ShoppingCart className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {syncResult.carts}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Carts</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-green-200 dark:border-green-700">
                  <Package className="w-5 h-5 text-orange-600 dark:text-orange-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {syncResult.abandonedCarts}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Abandoned</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center border border-green-200 dark:border-green-700">
                  <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {syncResult.consultations}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Consultations</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              disabled={syncing}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncResult ? 'Close' : 'Cancel'}
            </button>
            <button
              onClick={handleSmartSync}
              disabled={syncing || loading || (!syncCustomers && !syncCarts && !syncAbandonedCarts && !syncConsultations)}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 font-semibold shadow-lg shadow-indigo-600/30"
            >
              {syncing ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Syncing... {syncProgress}%
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Start Smart Sync
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartSyncModal;

